import { useState, useEffect, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';
import { Download, ChevronLeft, ChevronRight, Trash2, PenLine, Eraser, Type } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';
import { pdfjs } from '@/lib/pdfjs';
import { cn } from '@/lib/cn';

interface Stamp {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  pngBytes: Uint8Array;
}

interface CursiveFont {
  id: string;
  family: string;
  label: string;
  weight: number;
  /** Google Fonts family-name slug for stylesheet URL */
  url: string;
}

const FONTS: CursiveFont[] = [
  { id: 'caveat', family: 'Caveat', label: 'Casual', weight: 600, url: 'Caveat:wght@400;600;700' },
  { id: 'dancing', family: 'Dancing Script', label: 'Flowy', weight: 700, url: 'Dancing+Script:wght@400;600;700' },
  { id: 'allura', family: 'Allura', label: 'Elegant', weight: 400, url: 'Allura' },
  { id: 'great-vibes', family: 'Great Vibes', label: 'Wedding', weight: 400, url: 'Great+Vibes' },
  { id: 'sacramento', family: 'Sacramento', label: 'Single-stroke', weight: 400, url: 'Sacramento' },
  { id: 'satisfy', family: 'Satisfy', label: 'Friendly', weight: 400, url: 'Satisfy' },
  { id: 'kaushan', family: 'Kaushan Script', label: 'Brush', weight: 400, url: 'Kaushan+Script' },
  { id: 'homemade', family: 'Homemade Apple', label: 'Pen & paper', weight: 400, url: 'Homemade+Apple' },
];

let fontsInjected = false;
function injectFontStylesheet() {
  if (fontsInjected || typeof document === 'undefined') return;
  fontsInjected = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${FONTS.map((f) => `family=${f.url}`).join('&')}&display=swap`;
  document.head.appendChild(link);
}

/** Render the typed name to a trimmed transparent PNG. */
async function renderTypedSignature(
  text: string,
  font: CursiveFont,
  size: number,
  color: string,
  italic: boolean,
): Promise<Uint8Array> {
  // Measure first
  const measure = document.createElement('canvas').getContext('2d')!;
  const fontSpec = `${italic ? 'italic ' : ''}${font.weight} ${size}px "${font.family}", cursive`;
  measure.font = fontSpec;
  const m = measure.measureText(text);
  const padX = Math.max(20, size * 0.4);
  const padY = Math.max(14, size * 0.4);
  const w = Math.ceil(m.width + padX * 2);
  const h = Math.ceil(size * 1.5 + padY * 2);

  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.font = fontSpec;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, padX, h / 2);
  const blob: Blob = await new Promise((resolve) => c.toBlob((b) => resolve(b!), 'image/png'));
  return new Uint8Array(await blob.arrayBuffer());
}

export function PdfSigner() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState<{ width: number; height: number; img: string }[]>([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [signatureBytes, setSignatureBytes] = useState<Uint8Array | null>(null);
  const [stamps, setStamps] = useState<Stamp[]>([]);

  // Draw mode
  const [pen, setPen] = useState({ color: '#0a0a0b', width: 3 });
  const padRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const drawn = useRef(false);

  // Type mode
  const [typedName, setTypedName] = useState('');
  const [fontIdx, setFontIdx] = useState(0);
  const [typeSize, setTypeSize] = useState(72);
  const [typeColor, setTypeColor] = useState('#0a0a0b');
  const [typeItalic, setTypeItalic] = useState(false);

  const [working, setWorking] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    injectFontStylesheet();
  }, []);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize pad canvas
  useEffect(() => {
    const c = padRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    if (rect.width === 0) return;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [file]);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setStamps([]);
    setPageIdx(0);
    const buf = await readFileAsArrayBuffer(f);
    const doc = await pdfjs.getDocument({ data: buf }).promise;
    const list: typeof pages = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      const c = document.createElement('canvas');
      c.width = viewport.width;
      c.height = viewport.height;
      const ctx = c.getContext('2d')!;
      await page.render({ canvasContext: ctx, viewport, canvas: c }).promise;
      list.push({
        width: viewport.width,
        height: viewport.height,
        img: c.toDataURL('image/png'),
      });
    }
    setPages(list);
  }

  /* -------------------- Draw mode -------------------- */
  function padPos(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = padRef.current!;
    const rect = c.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }
  function padDown(e: React.PointerEvent<HTMLCanvasElement>) {
    drawing.current = true;
    drawn.current = true;
    const ctx = padRef.current!.getContext('2d')!;
    const p = padPos(e);
    ctx.strokeStyle = pen.color;
    ctx.lineWidth = pen.width;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }
  function padMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = padRef.current!.getContext('2d')!;
    const p = padPos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }
  function padUp() {
    drawing.current = false;
  }
  function padClear() {
    const c = padRef.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    drawn.current = false;
    setSignatureBytes(null);
  }

  async function captureDrawn() {
    if (!drawn.current) {
      toast.error('Draw a signature first');
      return;
    }
    const c = padRef.current!;
    const ctx = c.getContext('2d')!;
    const imgData = ctx.getImageData(0, 0, c.width, c.height);
    let minX = c.width;
    let minY = c.height;
    let maxX = 0;
    let maxY = 0;
    for (let y = 0; y < c.height; y++) {
      for (let x = 0; x < c.width; x++) {
        const a = imgData.data[(y * c.width + x) * 4 + 3];
        if (a > 10) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    const w = maxX - minX + 10;
    const h = maxY - minY + 10;
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    out.getContext('2d')!.drawImage(c, -minX + 5, -minY + 5);
    const blob: Blob = await new Promise((resolve) => out.toBlob((b) => resolve(b!), 'image/png'));
    setSignatureBytes(new Uint8Array(await blob.arrayBuffer()));
    toast.success('Signature ready — click on the page to place');
  }

  /* -------------------- Type mode -------------------- */
  async function captureTyped() {
    if (!typedName.trim()) {
      toast.error('Type your name first');
      return;
    }
    try {
      const bytes = await renderTypedSignature(
        typedName.trim(),
        FONTS[fontIdx],
        typeSize,
        typeColor,
        typeItalic,
      );
      setSignatureBytes(bytes);
      toast.success('Signature ready — click on the page to place');
    } catch {
      toast.error('Could not render typed signature');
    }
  }

  /* -------------------- Place + export -------------------- */
  function pagePos(e: React.PointerEvent<HTMLDivElement>) {
    const rect = pageRef.current!.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  }

  function placeStamp(e: React.PointerEvent<HTMLDivElement>) {
    if (!signatureBytes) return;
    const p = pagePos(e);
    setStamps((s) => [
      ...s,
      {
        page: pageIdx,
        x: p.x,
        y: p.y,
        w: 0.18,
        h: 0.06,
        pngBytes: signatureBytes,
      },
    ]);
  }

  function removeStamp(i: number) {
    setStamps((arr) => arr.filter((_, idx) => idx !== i));
  }

  async function exportPdf() {
    if (!file) return;
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(file);
      const doc = await PDFDocument.load(buf);
      const docPages = doc.getPages();
      for (const s of stamps) {
        const page = docPages[s.page];
        const { width, height } = page.getSize();
        const png = await doc.embedPng(s.pngBytes);
        page.drawImage(png, {
          x: s.x * width,
          y: height - (s.y + s.h) * height,
          width: s.w * width,
          height: s.h * height,
        });
      }
      const bytes = await doc.save();
      saveAs(bytesToBlob(bytes), file.name.replace(/\.pdf$/i, '') + '-signed.pdf');
      toast.success('Signed PDF saved');
    } finally {
      setWorking(false);
    }
  }

  const currentPage = pages[pageIdx];
  const pageStamps = stamps.filter((s) => s.page === pageIdx);

  return (
    <div>
      <ToolHeader
        toolId="pdf-signer"
        title="PDF signer"
        description="Draw a signature or type your name in cursive, then click on the PDF to stamp it. Export a signed copy."
        badges={
          stamps.length ? (
            <Badge>
              {stamps.length} signature{stamps.length === 1 ? '' : 's'}
            </Badge>
          ) : null
        }
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={handleFile} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:order-2 space-y-3">
            <Card>
              <Tabs defaultValue="draw">
                <TabsList className="w-full mb-3">
                  <TabsTrigger value="draw" className="flex-1">
                    <PenLine size={11} className="mr-1.5" />
                    Draw
                  </TabsTrigger>
                  <TabsTrigger value="type" className="flex-1">
                    <Type size={11} className="mr-1.5" />
                    Type
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="draw">
                  <canvas
                    ref={padRef}
                    onPointerDown={padDown}
                    onPointerMove={padMove}
                    onPointerUp={padUp}
                    onPointerLeave={padUp}
                    className="w-full h-32 rounded border border-border bg-bg-elev cursor-crosshair touch-none"
                  />
                  <div className="grid grid-cols-2 gap-1.5 mt-2">
                    <Field label="Pen color" className="!mb-0">
                      <input
                        type="color"
                        value={pen.color}
                        onChange={(e) => setPen({ ...pen, color: e.target.value })}
                        className="!w-full !h-8"
                      />
                    </Field>
                    <Field label="Stroke" className="!mb-0">
                      <Slider
                        value={pen.width}
                        onChange={(v) => setPen({ ...pen, width: v })}
                        min={1}
                        max={8}
                        showValue={false}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button size="sm" variant="ghost" onClick={padClear}>
                      <Eraser size={11} />
                      Clear
                    </Button>
                    <Button size="sm" variant="primary" onClick={captureDrawn}>
                      <PenLine size={11} />
                      Use
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="type" className="space-y-3">
                  <Field label="Your name">
                    <Input
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Type your full name…"
                    />
                  </Field>
                  <div>
                    <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-1.5">Style</div>
                    <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-1">
                      {FONTS.map((f, i) => (
                        <button
                          key={f.id}
                          onClick={() => setFontIdx(i)}
                          className={cn(
                            'flex flex-col items-center justify-center px-2 py-2 rounded-md border-2 transition',
                            fontIdx === i
                              ? 'border-accent bg-accent/10'
                              : 'border-border hover:border-border-strong',
                          )}
                        >
                          <span
                            className="text-lg leading-none"
                            style={{
                              fontFamily: `"${f.family}", cursive`,
                              fontWeight: f.weight,
                              fontStyle: typeItalic ? 'italic' : 'normal',
                              color: typeColor,
                            }}
                          >
                            {typedName.split(' ')[0] || 'Name'}
                          </span>
                          <span className="text-2xs text-fg-subtle mt-1 uppercase tracking-wider">
                            {f.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <Field label="Color" className="!mb-0">
                      <input
                        type="color"
                        value={typeColor}
                        onChange={(e) => setTypeColor(e.target.value)}
                        className="!w-full !h-8"
                      />
                    </Field>
                    <Field label="Size" hint={`${typeSize}px`} className="!mb-0">
                      <Slider
                        value={typeSize}
                        onChange={setTypeSize}
                        min={36}
                        max={140}
                        step={2}
                        showValue={false}
                      />
                    </Field>
                  </div>
                  <label className="flex items-center justify-between text-sm cursor-pointer">
                    <span className="text-fg-muted">Slanted</span>
                    <input
                      type="checkbox"
                      checked={typeItalic}
                      onChange={(e) => setTypeItalic(e.target.checked)}
                      className="size-4 accent-accent"
                    />
                  </label>
                  <Button
                    size="sm"
                    variant="primary"
                    className="w-full"
                    disabled={!typedName.trim()}
                    onClick={captureTyped}
                  >
                    <Type size={11} />
                    Use this signature
                  </Button>
                </TabsContent>
              </Tabs>

              {signatureBytes && (
                <div className="mt-3 px-2.5 py-1.5 rounded text-2xs text-success bg-success/10 border border-success/30 text-center">
                  Signature ready — click on the page to place
                </div>
              )}
            </Card>

            <Card className="space-y-2">
              <div className="flex items-center justify-between">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={pageIdx === 0}
                  onClick={() => setPageIdx((i) => Math.max(0, i - 1))}
                >
                  <ChevronLeft size={12} />
                </Button>
                <span className="text-xs font-mono">
                  {pageIdx + 1} / {pages.length}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={pageIdx >= pages.length - 1}
                  onClick={() => setPageIdx((i) => Math.min(pages.length - 1, i + 1))}
                >
                  <ChevronRight size={12} />
                </Button>
              </div>
              <Button
                variant="primary"
                size="sm"
                className="w-full"
                loading={working}
                disabled={!stamps.length || working}
                onClick={exportPdf}
              >
                <Download size={11} />
                Export signed
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setFile(null);
                  setStamps([]);
                }}
              >
                Change file
              </Button>
            </Card>
          </div>

          <Card className="lg:col-span-2 lg:order-1 !p-3 overflow-hidden">
            {currentPage && (
              <div
                ref={pageRef}
                onClick={(e) => placeStamp(e as unknown as React.PointerEvent<HTMLDivElement>)}
                className={cn(
                  'relative inline-block max-w-full mx-auto rounded shadow-card touch-none select-none',
                  signatureBytes && 'cursor-crosshair',
                )}
                style={{ width: '100%', maxWidth: currentPage.width }}
              >
                <img
                  src={currentPage.img}
                  alt={`page ${pageIdx + 1}`}
                  className="w-full h-auto block pointer-events-none"
                />
                {pageStamps.map((s, i) => (
                  <div
                    key={i}
                    className="absolute group"
                    style={{
                      left: `${s.x * 100}%`,
                      top: `${s.y * 100}%`,
                      width: `${s.w * 100}%`,
                      height: `${s.h * 100}%`,
                    }}
                  >
                    <img
                      src={URL.createObjectURL(new Blob([new Uint8Array(s.pngBytes)], { type: 'image/png' }))}
                      alt="signature"
                      className="w-full h-full object-contain pointer-events-none"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const idx = stamps.findIndex((x) => x === s);
                        if (idx >= 0) removeStamp(idx);
                      }}
                      className="absolute -top-2 -right-2 size-5 rounded-full bg-danger text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                    >
                      <Trash2 size={9} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default PdfSigner;
